import React from 'react';
import { getRowStatus, getStatusIcon } from '../utils/rowUtils';
import HintHelper from '../../HintHelper/HintHelper';


const TableRow = ({
  title,
  row,
  originalIndex,
  calculateColumnWidths,
  splitMode,
  fixedNetworkAdjustmentFactors,
  subdivisionWidths,
  useCustomWidths,
  hints,
  tempValues,
  handleCellChange,
  handleCellBlur,
  handleKeyDown,
  handlePaste,
  handleFocus,
  rowHeights,
  rowName,
  startRowResize,
  firstColumnWidth
}) => {
  const { isRowEmpty, isRowIncomplete, isRowComplete } = getRowStatus(row);
  const isMobile = window.innerWidth < 768;
  const shouldSpace = title === 'Serviços de Apoio Diagnóstico e Terapias Ambulatoriais' && row[0][0] && !row[0][0].toLowerCase().includes("serviço");

  const formatDecimalInput = (value) => {
    if (!value) return "";

    let formatted = value.replace(",", ".");
    formatted = formatted.replace(/[^0-9.]/g, "");

    const parts = formatted.split(".");
    if (parts.length > 2) {
      formatted = parts[0] + "." + parts.slice(1).join("");
    }

    if (formatted.startsWith(".")) {
      formatted = "0" + formatted;
    }

    return formatted;
  };

  const formatIntegerInput = (value) => {
    if (!value) return "";

    let numeric = value.replace(/\D/g, "");

    if (numeric !== "") {
      const number = parseInt(numeric, 10);
      if (number > 100) {
        numeric = "100";
      }
    }

    return numeric;
  };

  return (
    <div className="flex relative">
      <div
        className={`bg-gray-50 border-t border-r-2 border-gray-300 flex items-center text-sm ${shouldSpace && 'pl-8'} px-2 gap-2 sm:text-md static md:sticky left-0 z-10 sm:text-md`}
        style={{
          width: useCustomWidths ? calculateColumnWidths[0] + 'px' : firstColumnWidth + 'px',
          height: useCustomWidths ? (rowHeights?.[rowName] || 40) + 'px' : 40 + 'px',
        }}
      >
        {getStatusIcon(0, isRowEmpty, isRowIncomplete, isRowComplete)}
        <input
          type="text"
          value={row[0].slice(0, 1)}
          readOnly
          className="bg-transparent border-none outline-none flex-1 font-semibold text-sm pr-6"
          title={row[0]}
          style={{
            width: useCustomWidths ? calculateColumnWidths[0] + 'px' : firstColumnWidth + 'px',
            height: useCustomWidths ? (rowHeights?.[rowName] || 40) + 'px' : 40 + 'px',
          }}
        />
        {hints[originalIndex]?.[0] && (
          <HintHelper
            className="right-1 relative z-50"
            message={hints[originalIndex]}
            messageStyle={
              originalIndex === 0
                ? 'top-0 left-10'
                : originalIndex === hints.length - 1
                  ? '-top-12 left-10'
                  : '-top-10 left-10'
            }
          />
        )}
        {useCustomWidths && !isMobile && (
          <div
            className="absolute bottom-0 left-0 w-full h-1 cursor-row-resize hover:bg-blue-400"
            onMouseDown={(e) => startRowResize(e, originalIndex)}
          />
        )}
      </div>

      {row.slice(1).map((cell, index) => {
        const colIndex = index + 1;
        let cellValue = Array.isArray(cell) ? Array.from(cell) : [cell];

        if (splitMode && Array.isArray(cellValue) && fixedNetworkAdjustmentFactors[originalIndex] === true) {
          cellValue[1] = '100';
        }

        const tempKey0 = `${originalIndex}-${colIndex}-0`;
        const tempKey1 = `${originalIndex}-${colIndex}-1`;
        const displayValue0 = tempValues[tempKey0] !== undefined ? tempValues[tempKey0] : cellValue[0] ?? "";
        const displayValue1 = tempValues[tempKey1] !== undefined ? tempValues[tempKey1] : cellValue[1] ?? "";

        return (
          <div
            key={colIndex}
            className={`border text-wrap text-base ${(((cell[0] !== "" || cell[1] !== "") || cell === '')) ? 'bg-white' : ''}`}
            style={{
              width: colIndex !== 1 ? calculateColumnWidths[colIndex] + 'px' : (calculateColumnWidths[colIndex] - 1) + 'px',
              height: useCustomWidths ? (rowHeights?.[rowName] || 40) + 'px' : 40 + 'px',
            }}
          >
            <div className="flex flex-row w-full h-full relative">
              {splitMode ? (
                <>
                  <input
                    id={`input-${originalIndex}-${colIndex}-0`}
                    type="text"
                    value={displayValue0?.toString().replace('.', ',')}
                    onChange={(e) => {
                      const formatted = formatDecimalInput(e.target.value);
                      handleCellChange(
                        originalIndex,
                        colIndex,
                        formatted.replace(',', '.'),
                        0
                      );
                    }}
                    onBlur={() => handleCellBlur(originalIndex, colIndex, 0)}
                    onKeyDown={(e) => handleKeyDown(e, originalIndex, colIndex, 0)}
                    onPaste={(e) => handlePaste(originalIndex, colIndex, e)}
                    className="bg-transparent border-none focus:outline-none text-ellipsis px-2"
                    style={{ width: colIndex !== 1 ? subdivisionWidths[colIndex].city : (subdivisionWidths[colIndex].city - 1) }}
                    autoComplete="off"
                    onFocus={(e) => handleFocus(e.target)}
                  />
                  <input
                    id={`input-${originalIndex}-${colIndex}-1`}
                    type="text"
                    value={displayValue1?.toString()}
                    onChange={(e) => {
                      const formatted = formatIntegerInput(e.target.value);
                      handleCellChange(
                        originalIndex,
                        colIndex,
                        formatted,
                        1
                      );
                    }}
                    onBlur={() => handleCellBlur(originalIndex, colIndex, 1)}
                    onKeyDown={(e) => handleKeyDown(e, originalIndex, colIndex, 1)}
                    className="bg-transparent border-l-2 focus:outline-none text-gray-400 px-2"
                    style={{ width: subdivisionWidths[colIndex].fao }}
                    autoComplete="off"
                    readOnly={fixedNetworkAdjustmentFactors[originalIndex] === true}
                    onFocus={(e) => handleFocus(e.target)}
                  />
                  <span className="absolute right-1 top-1/2 -translate-y-1/2 font-medium text-gray-600">%</span>
                </>
              ) : (
                <input
                  id={`input-${originalIndex}-${colIndex}-0`}
                  type="text"
                  value={cell?.toString().replace('.', ',') ?? ''}
                  onChange={(e) => {
                    const formatted = formatDecimalInput(e.target.value);
                    handleCellChange(
                      originalIndex,
                      colIndex,
                      formatted.replace(',', '.'),
                      0
                    );
                  }}
                  onBlur={() => handleCellBlur(originalIndex, colIndex, 0)}
                  onKeyDown={(e) => handleKeyDown(e, originalIndex, colIndex, 0)}
                  onPaste={(e) => handlePaste(originalIndex, colIndex, e)}
                  className="bg-transparent border-none focus:outline-none w-full"
                  autoComplete="off"
                  onFocus={(e) => handleFocus(e.target)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default React.memo(TableRow);
